import { all, call, fork, put, takeEvery, select } from 'redux-saga/effects';
import { deleteDocuments, getCommentsOrReports, uploadCommentOrReport as uploadCommentOrReportApi, getDocumentTypes } from '../../utils/api/documentsApi';
import { deleteCommentOrReport, deleteCommentOrReportError, fetchCommentOrReport, fetchCommentOrReportError, fetchCommentOrReportSuccess, uploadCommentOrReport, uploadCommentOrReportError, uploadCommentOrReportSuccess, fetchDocumentTypesError, fetchDocumentTypesSuccess, deleteCommentOrReportSuccess } from './actions';
import { createDeleteDocumentRequest } from '../shared/model';
import { DetailsCommentOrReportActionTypes } from './types';
import { toggleIsUIDisabled } from '../../shared/actions';
import * as selectors from './selectors';
import { SagaIterator } from 'redux-saga';

function* handleFetchCommentOrReport(action: ReturnType<typeof fetchCommentOrReport>): SagaIterator {
  try {
    yield put(toggleIsUIDisabled(true));
    const res = yield call(getCommentsOrReports, action.payload.instructionId);

    if (!res.data) {
      yield put(fetchCommentOrReportError(JSON.stringify(res.response)));
    } else {
      yield put(fetchCommentOrReportSuccess(res.data));
    }
  } catch (err) {
    if (err instanceof Error) {
      yield put(fetchCommentOrReportError(`${err.message}\n${err.name}\n${err.stack}`));
    } else {
      yield put(fetchCommentOrReportError('An unknown error occured.'));
    }
  } finally {
    yield put(toggleIsUIDisabled(false));
  }
}

function* handleFetchDocumentTypes(): SagaIterator {
  try {
    const res = yield call(getDocumentTypes);

    if (!res) {
      yield put(fetchDocumentTypesError(JSON.stringify(res.response)));
    } else {
      yield put(fetchDocumentTypesSuccess(res));
    }
  } catch (err) {
    if (err instanceof Error) {
      yield put(fetchDocumentTypesError(`${err.message}\n${err.name}\n${err.stack}`));
    } else {
      yield put(fetchDocumentTypesError('An unknown error occured.'));
    }
  }
}

function* handleUploadCommentOrReport(action: ReturnType<typeof uploadCommentOrReport>): SagaIterator {
  try {
    yield put(toggleIsUIDisabled(true));
    const commentOrReport = yield select(selectors.commentOrReport, action.payload.index);
    const claimNotificationId = yield select(selectors.claimNotificationId);

    const localFileContent = atob(commentOrReport.content);
    if (localFileContent.charAt(0) === 'M' && localFileContent.charAt(1) === 'Z') {
      const err = Error('Please provide a correct type of document!');
      throw(err);
    }
    
    // Sanitize filename
    const sanitizedFilename = sanitizeFilename(commentOrReport.filename);
    const sanitizedCommentOrReport = { ...commentOrReport, filename: sanitizedFilename };

    const res = yield call(uploadCommentOrReportApi, action.payload.instructionId, { claimNotificationId, documents: [sanitizedCommentOrReport] });

    if (!res.data) {
      yield put(uploadCommentOrReportError(JSON.stringify(res.response)));
    } else {
      yield put(uploadCommentOrReportSuccess(action.payload.index, res.data[0]));
    }
  } catch (err) {
    if (err instanceof Error) {
      yield put(uploadCommentOrReportError(`${err.message}\n${err.name}\n${err.stack}`));
    } else {
      yield put(uploadCommentOrReportError('An unknown error occured.'));
    }
  } finally {
    yield put(toggleIsUIDisabled(false));
  }
}

function* handleDeleteCommentOrReport(action: ReturnType<typeof deleteCommentOrReport>): SagaIterator {
  yield put(toggleIsUIDisabled(true));
  const commentOrReport = yield select(selectors.commentOrReport, action.payload.index);
  try {
    if (commentOrReport.isOnline) {
      const deleteRequest = createDeleteDocumentRequest();
      deleteRequest.amazonId = commentOrReport.amazonId;
      deleteRequest.id = commentOrReport.id;

      const res = yield call(deleteDocuments, [deleteRequest]);

      if (!res.data) {
        yield put(deleteCommentOrReportError(JSON.stringify(res.response)));
      } else {
        yield put(deleteCommentOrReportSuccess(action.payload.index));
      }
    } else {
      yield put(deleteCommentOrReportSuccess(action.payload.index));
    }
  } catch (err) {
    if (err instanceof Error) {
      yield put(deleteCommentOrReportError(`${err.message}\n${err.name}\n${err.stack}`));
    } else {
      yield put(deleteCommentOrReportError('An unknown error occured.'));
    }
  } finally {
    yield put(toggleIsUIDisabled(false));
  }
}

function sanitizeFilename(filename: string): string {
  if (!filename || filename.trim() === '') {
    return ''; // or any default fallback name you prefer
  }
  // Normalize to NFD form and remove diacritics
  return filename
    .normalize('NFD') // Split accented characters into base + diacritic
    .replace(/[\u0300-\u036f]/g, '') // Remove diacritic marks
    .replace(/[^\x00-\x7F]/g, '') // Remove any remaining non-ASCII characters
    .replace(/[^a-zA-Z0-9._-]/g, '_'); // Replace unsafe characters with underscore
}

function* watchOtherAttachmentsDetails() {
  yield takeEvery(DetailsCommentOrReportActionTypes.FETCH_COMMENT_OR_REPORT, handleFetchCommentOrReport);
  yield takeEvery(DetailsCommentOrReportActionTypes.UPLOAD_COMMENT_OR_REPORT, handleUploadCommentOrReport);
  yield takeEvery(DetailsCommentOrReportActionTypes.DELETE_COMMENT_OR_REPORT, handleDeleteCommentOrReport);
  yield takeEvery(DetailsCommentOrReportActionTypes.FETCH_DOCUMENT_TYPES, handleFetchDocumentTypes);
}

function* detailsOtherAttachmentsSaga() {
  yield all([fork(watchOtherAttachmentsDetails)]);
}

export default detailsOtherAttachmentsSaga;
